import React, { useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Card, Toggle, FieldLabel, inputClass, textareaClass, StatusPill } from './walletUi';
import { LockScreenPreview } from './WalletPassPreview';
import { personalisationFields, fillPlaceholders } from '../../data/mockDigitalWallet';
interface Automation {
  id: string;
  name: string;
  trigger: string;
  timing: string;
  title: string;
  message: string;
  on: boolean;
  phase2?: boolean;
  klaviyoNote?: string;
}
const initial: Automation[] = [{
  id: 'welcome',
  name: 'Welcome',
  trigger: 'After the pass is added to a wallet',
  timing: '1 hour after',
  title: 'Welcome, {{firstName}}',
  message: 'Your member pass is ready. Show it at the cellar door to earn points on every visit.',
  on: true
}, {
  id: 'tierUp',
  name: 'Tier up',
  trigger: 'When a member moves up a tier',
  timing: 'Straight away',
  title: "You've reached {{tier}}",
  message: 'Nice work {{firstName}}. Open your pass to see what {{tier}} members get.',
  on: true,
  phase2: true,
  klaviyoNote: 'Your Klaviyo Tier Changed flow keeps running alongside this.'
}, {
  id: 'rewardReady',
  name: 'Reward ready',
  trigger: 'When a member can claim a new reward',
  timing: 'Straight away',
  title: 'A reward is waiting for you',
  message: 'You have {{rewards}} rewards ready. Open your pass or the member portal to use them.',
  on: true,
  phase2: true,
  klaviyoNote: 'Your Klaviyo Reward Available flow keeps running alongside this.'
}, {
  id: 'pointsExpiring',
  name: 'Points expiring',
  trigger: 'Before points expire',
  timing: '14 days before',
  title: 'Your points expire soon',
  message: 'You have {{points}} points that expire in 14 days. Use them on your next order.',
  on: false,
  phase2: true,
  klaviyoNote: 'Uses the expiry warning from Program Settings.'
}, {
  id: 'pointsEarned',
  name: 'Points earned',
  trigger: 'When a member earns points on an order',
  timing: 'Straight away',
  title: 'Points added',
  message: 'You now have {{points}} points. {{nextTier}} is getting closer.',
  on: false,
  phase2: true
}];
export function WalletAutomationsTab({
  onToast
}: {
  onToast: (msg: string) => void;
}) {
  const [items, setItems] = useState(initial);
  const [editing, setEditing] = useState<Automation | null>(null);
  const toggle = (id: string, on: boolean) => {
    setItems(prev => prev.map(a => a.id === id ? {
      ...a,
      on
    } : a));
    onToast(on ? 'Automation turned on' : 'Automation turned off');
  };
  const section = (title: string, description: string, list: Automation[]) => <Card title={title} description={description} data-id="element-2316" data-mp="3hj4267tx3gus">
      <div className="divide-y divide-[#F1F5F9]" data-id="element-2317" data-mp="3mfuj0xdjqgh9">
        {list.map(a => <div key={a.id} className="py-4 flex items-start gap-4" data-id="element-2318" data-mp="3me19997mzg51">
            <Toggle on={a.on} onChange={v => toggle(a.id, v)} label={a.name} data-id="element-2319" data-mp="38h4kddb2w8qs" />
            <div className="flex-1 min-w-0" data-id="element-2320" data-mp="3ch4xmumfydw9">
              <div className="flex items-center gap-2" data-id="element-2321" data-mp="3ckaks4bif5pc">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-2322" data-mp="2tuxdly4a5rnk">{a.name}</span>
                {a.phase2 && <StatusPill status="Phase 2" data-id="element-2323" data-mp="2ovuk9axnx9vf" />}
              </div>
              <div className="text-xs text-[#64748B]" data-id="element-2324" data-mp="32uc61340ccap">
                {a.trigger} · {a.timing}
              </div>
              <div className="text-sm text-[#475569] mt-1 truncate" data-id="element-2325" data-mp="2xv7wuch32ekd">
                <span className="font-medium text-[#0F172A]" data-id="element-2326" data-mp="3csrwr4z4hjjo">{a.title}</span> · {a.message}
              </div>
              {a.klaviyoNote && <div className="text-xs text-[#94A3B8] mt-1" data-id="element-2327" data-mp="37ugnavcqpb1z">{a.klaviyoNote}</div>}
            </div>
            <Button variant="secondary" size="sm" iconLeft={<Pencil className="w-4 h-4" data-id="element-2329" data-mp="3lka9t70rvj8j" />} onClick={() => setEditing(a)} data-id="element-2328" data-mp="3lllhvj193d5g">
              Edit
            </Button>
          </div>)}
      </div>
    </Card>;
  return <div className="space-y-6" data-id="element-2330" data-mp="3vigefwdyr3h3">
      {section('Pass messages', 'Sent automatically to every member with a pass.', items.filter(a => !a.phase2))}
      {section('Loyalty moments', 'Sent when something changes on a member\'s loyalty account. The pass updates and the push arrives together.', items.filter(a => a.phase2))}

      {editing && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setEditing(null)} data-id="element-2331" data-mp="igdusmdesppf" />
          <div className="fixed top-0 right-0 h-full w-[520px] bg-white shadow-xl z-50 flex flex-col" data-id="element-2332" data-mp="n9yuqjykbtr9">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-2333" data-mp="sae83biuh198">
              <h2 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-2334" data-mp="3rphzf5if7klj">{editing.name}</h2>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-[#F1F5F9]" aria-label="Close" data-id="element-2335" data-mp="3vpa8rkfn1ury">
                <X className="w-5 h-5 text-[#64748B]" data-id="element-2336" data-mp="9hshb5mso0vi" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-2337" data-mp="9ffsq9mj6s9h">
              <div className="text-sm text-[#475569]" data-id="element-2338" data-mp="1fxck9wzxln1c">{editing.trigger}</div>
              {editing.id === 'welcome' && <div className="grid grid-cols-2 gap-3" data-id="element-2339" data-mp="1kv4eixo1tl8p">
                  <div data-id="element-2340" data-mp="35f9hg192wxcw">
                    <FieldLabel data-id="element-2341" data-mp="35doqellz3wjs">Send after</FieldLabel>
                    <input className={inputClass} defaultValue="1" data-id="element-2342" data-mp="30g6pnii40nmv" />
                  </div>
                  <div data-id="element-2343" data-mp="2vp6qs7pfjvhq">
                    <FieldLabel data-id="element-2344" data-mp="2voewd07kk3t1">&nbsp;</FieldLabel>
                    <select className={inputClass} defaultValue="Hours" data-id="element-2345" data-mp="2vn3pusmqohy8">
                      <option data-id="element-2346" data-mp="2qpkeq1lc8lai">Minutes</option>
                      <option data-id="element-2347" data-mp="2moidjf7v1z5w">Hours</option>
                      <option data-id="element-2348" data-mp="2mp9y7uzx6che">Days</option>
                    </select>
                  </div>
                </div>}
              {editing.id === 'pointsExpiring' && <div data-id="element-2349" data-mp="2cqcct0xsiz2m">
                  <FieldLabel hint="Matches the expiry warning in Program Settings." data-id="element-2350" data-mp="2cpstpgwem4n0">Days before expiry</FieldLabel>
                  <input className={inputClass} defaultValue="14" data-id="element-2351" data-mp="27saskcomotpk" />
                </div>}
              <div data-id="element-2352" data-mp="az7mc87lg4jv">
                <FieldLabel data-id="element-2353" data-mp="axo7mm1l61k7">Title</FieldLabel>
                <input className={inputClass} value={editing.title} onChange={e => setEditing({
              ...editing,
              title: e.target.value
            })} data-id="element-2354" data-mp="5zv09pojr1ic" />
              </div>
              <div data-id="element-2355" data-mp="20ea10qhag44n">
                <FieldLabel data-id="element-2356" data-mp="257bgazleq5or">Message</FieldLabel>
                <textarea rows={3} className={textareaClass} value={editing.message} onChange={e => setEditing({
              ...editing,
              message: e.target.value
            })} data-id="element-2357" data-mp="258e8rbydog6s" />
                <div className="flex flex-wrap gap-1.5 mt-1" data-id="element-2358" data-mp="1vhjh1s2d4ody">
                  {personalisationFields.map(f => <button key={f.key} type="button" onClick={() => setEditing({
                ...editing,
                message: `${editing.message}{{${f.key}}}`
              })} className="px-2 py-0.5 text-xs rounded border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]" data-id="element-2359" data-mp="2j743ikxq63rz">
                      {f.label}
                    </button>)}
                </div>
              </div>
              <div className="flex justify-center pt-2" data-id="element-2360" data-mp="ypoecyp53g22">
                <LockScreenPreview title={fillPlaceholders(editing.title)} message={fillPlaceholders(editing.message)} platform="apple" data-id="element-2361" data-mp="12scm1324agxg" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-end gap-2" data-id="element-2362" data-mp="vawl8unsbcj1">
              <Button variant="ghost" onClick={() => setEditing(null)} data-id="element-2363" data-mp="q9gz0z9vezz2">
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
            setItems(prev => prev.map(a => a.id === editing.id ? editing : a));
            setEditing(null);
            onToast('Automation saved');
          }} data-id="element-2364" data-mp="154n6lqom76ui">
                Save
              </Button>
            </div>
          </div>
        </>}
    </div>;
}